import { cn } from "@/utils/theme";
import { Avatar, Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { useState } from "react";
import type { SimpleNodeSerialized } from "..";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { Operations } from "../../calculated-field/CalculatedFieldOperation/utils/operations";
import { parseType } from "../../data-center/serialization/deserialization";
import { GeneralVariableKeyBox } from "../key-box";

const nodeTypes = (node: string) => {
  switch (node) {
    case NodeTypes.FORM:
      return [
        "action",
        "bars",
        "bg-surface-action-accent-quiet border-action-quiet text-action-quiet",
        "Formulario",
      ] as const;
    case NodeTypes.IF:
      return [
        "neutral",
        "split",
        "bg-neutral-object-accent-silent border-neutral text-body",
        "Operador IF",
      ] as const;
    case NodeTypes.SWITCH:
      return [
        "neutral",
        "arrows-cross",
        "bg-neutral-object-accent-silent border-neutral text-body",
        "Operador Switch",
      ] as const;
    case NodeTypes.CALCULATED:
      return [
        "success",
        "function",
        "bg-surface-sucess-accent-quiet border-contextual-success text-success",
        "Campo Calculado",
      ] as const;
    case NodeTypes.ASSIGN_GROUP:
      return [
        "info",
        "users",
        "bg-surface-info-accent-quiet border-contextual-info text-info",
        "Asignar Grupo",
      ] as const;
    case NodeTypes.DATA_CENTER:
      return [
        "info",
        "table",
        "bg-surface-info-accent-quiet border-contextual-info-quiet text-info-quiet",
        "Centro de Datos",
      ] as const;
    case NodeTypes.DOC_GENERATOR:
      return [
        "action",
        "file-alt",
        "bg-surface-action-accent-quiet border-contextual-action-quiet text-action-quiet",
        "Generador de Documentos",
      ] as const;
    default:
      return [
        "neutral",
        "bars",
        "bg-surface-action-accent-quiet border-action-quiet text-action-quiet",
        "Desconocido",
      ] as const;
  }
};

interface Props {
  data: SimpleNodeSerialized;
}

const GeneralInput = ({ data }: Props) => {
  const [isOpen, setIsOpen] = useState(true);
  const nodeConfig = nodeTypes(data.node_type);
  return (
    <div className="text-body">
      <div
        className={cn(
          "pl-2.5 space-x-2 flex items-center justify-start",
          isOpen ? "pb-0" : "pb-8",
        )}
      >
        <div className="bg-transparent p-1 border border-neutral-300 rounded-[12px]">
          <Avatar
            size="2xs"
            shape={null}
            className={cn(
              "items-center justify-center border rounded-lg",
              nodeConfig[2],
            )}
          >
            <div className="">
              <Icon type="fas" name={nodeConfig[1]} size="sm" className="" />
            </div>
          </Avatar>
        </div>
        <div className="flex w-full items-center space-x-2 min-w-0">
          <Badge size="sm" variant={nodeConfig[0]}>
            {nodeConfig[3]}
          </Badge>

          <div className="flex-1 font-semibold text-body-loud truncate">
            {data.node_name}
          </div>

          <Button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle"
            size="icon"
            variant="ghost"
            className={`ml-auto mr-3 ${isOpen ? "rotate-0" : "rotate-180"}`}
          >
            <Icon name="angle-up" type="fas" />
          </Button>
        </div>
      </div>
      {isOpen && (
        <div className="pr-4 pt-2 pb-4">
          <NodeBox id={data.node_id} />
        </div>
      )}
    </div>
  );
};

export default GeneralInput;

const NodeBox = ({ id }: { id: string }) => {
  const { getNodeById } = useWorkflowStore();
  const node = getNodeById(id);
  if (!node) return <></>;
  const nodeData = node.data as unknown as NodeData;

  const type = nodeData?.inputs[0]?.calculations?.[0].inputs[0].type;
  const operation = nodeData?.inputs[0]?.calculations?.[0].operation;
  const outputType = Operations[
    parseType(type?.toLowerCase() || "") as keyof typeof Operations
  ]?.find((op) => op.name === operation?.toLowerCase())?.output;

  return (
    <div className="flex">
      <div
        className={cn("w-0.5 relative translate-x-[25px]", "bg-transparent")}
      >
        <span className="absolute left-3 top-[14px] w-2 h-2 bg-neutral-300 rounded-full"></span>
        <span className="absolute w-3 h-5 border-l-2 border-t-2 border-neutral-300 rotate-x-180 rounded-tl-xl"></span>
      </div>

      <div className="relative pl-[50px] mb-4 w-full">
        <div className="py-1">
          <div className="space-y-3">
            <GeneralVariableKeyBox
              node_id={id}
              name={nodeData.displayName}
              type={outputType}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
